import userData from '../user-data';
import type { VehicleNA, TireSize } from '../user-data';
import fitmentApi from './fitment-api';
import type { TireSelector } from './tireselector';
import { t } from '../i18n';
import { isInViewport } from '../utils';
import Mustache from 'mustache';
import AutocompleteCombobox from '../combobox/autocomplete-combobox';

export type State = {
  year: string | null,
  manufacturer: string | null,
  model: string | null,
  submodel: string | null,
};

class ModelNA {
  dom: {
    root: HTMLElement, // This module's root element
    form: HTMLFormElement,
    formSubmitBtn: HTMLButtonElement,
    year: HTMLSelectElement,
    manufacturer: HTMLSelectElement,
    model: HTMLSelectElement,
    submodel: HTMLSelectElement,
    errorMessage: HTMLElement,
    sizesContainer: HTMLElement,
  };

  // Tyre selector filter form state
  #state: State = {
    year: null,
    manufacturer: null,
    model: null,
    submodel: null,
  };

  // Fitment api uses names as ids for all other fields, except submodel.
  // So cache submodel name here for display purposes.
  #idNameCache: {
    submodel?: Record<string, string>,
  } = {};

  #loading = false;

  #comboboxes:any = {};
  #updatingFilters = false;

  availableSizes: TireSize[] = [];
  parent: TireSelector;
  name = 'ModelNA';

  constructor(element: HTMLElement, parent: TireSelector) {
    this.dom = {
      root: element,
      form: element.querySelector('form[name="modelSearchForm"]') as HTMLFormElement,
      formSubmitBtn: element.querySelector('button[type="submit"]') as HTMLButtonElement,
      year: element.querySelector('select[name="year"]') as HTMLSelectElement,
      manufacturer: element.querySelector('select[name="manufacturer"]') as HTMLSelectElement,
      model: element.querySelector('select[name="model"]') as HTMLSelectElement,
      submodel: element.querySelector('select[name="submodel"]') as HTMLSelectElement,
      errorMessage: element.querySelector('[data-error-message]') as HTMLElement,
      sizesContainer: element.querySelector('[data-sizes-container]') as HTMLElement,
    };

    this.parent = parent;
    this.name = `${this.parent.name} ${this.name}`;

    if (this.parent && Object.keys(this.dom).every(key => this.dom[key])) {
      // Necessary data and dom elements found
      console.log(`[${this.name}].init()`);
      this.log('init settings', this);
      this.initState();
      this.initCustomDropdowns();
      this.attachEventListeners();
    } else {
      console.error('[TS ModelNA]: missing data or dom elements', this);
    }
  }

  /**
   * Initialises state from server or userData if available
   */
  protected initState() {
    // Default state is empty
    const initialState = this.getState();

    // Check values from select elements (values set by server)
    Object.keys(this.#state).forEach(key => {
      if(this.dom[key]) {
        const value = this.getSelectValue(this.dom[key]);
        initialState[key] = value;

        // Set all except year to disabled initially
        if (key !== 'year') {
          this.dom[key].disabled = true;
        }
      }
    });

    // Restore state from userData
    this.restoreStateFromUserData(initialState);

    this.setState(initialState);
  }

  /**
   * Restores state from persistent storage (userData).
   * Override in subclasses to disable or customize restoration.
   */
  protected restoreStateFromUserData(state: State) {
    const userVehicleNA = userData.get('vehicleNA');
    if (userVehicleNA) {
      Object.keys(userVehicleNA).forEach(key => {
        if (this.dom[key]) {
          state[key] = userVehicleNA[key];
        }
      });
    }
  }

  /**
   * Returns current state of size filters
   */
  getState() {
    return {...this.#state };
  }

  /**
   * Sets new state and updates form fields accordingly:
   * Sets available options for each field based on other field values.
   * Sets enabled/disabled state of fields.
   * Sets submit button enabled/disabled state.
   */
  async setState(newState: State) {
    const oldState = this.getState();
    const futureState = { ...oldState, ...newState };

    this.log('updating state', { oldState, futureState, changed: newState });

    // Set internal state
    this.#state = futureState;
    let yearChanged = false;
    let manufacturerChanged = false;
    let modelChanged = false;

    // Populate select options (year options come from server intitially)
    if (this.#state.year !== oldState.year) {
      yearChanged = true;
      this.clearSelectOptions(this.dom.manufacturer);
      this.clearSelectOptions(this.dom.model);
      this.clearSelectOptions(this.dom.submodel);

      if(this.#state.year) {
        this.dom.year.value = this.#state.year;
        this.setSelectLoading(this.dom.manufacturer, true);
        try {
          const manufacturers = await fitmentApi.getNAManufacturers(this.#state.year);
          this.addSelectOptions(this.dom.manufacturer, manufacturers);
        } catch (error) {
          console.error(`[${this.name}] Error fetching manufacturers:`, error);
        } finally {
          this.setSelectLoading(this.dom.manufacturer, false);
        }
      } else {
        this.dom.year.value = '';
      }
    }

    // Switching year but choosing same manufacturer should still trigger manufacturer change to fetch models for new year
    if (yearChanged || this.#state.manufacturer !== oldState.manufacturer) {
      manufacturerChanged = true;
      this.clearSelectOptions(this.dom.model);
      this.clearSelectOptions(this.dom.submodel);

      if (this.#state.year && this.#state.manufacturer) {
        this.dom.manufacturer.value = this.#state.manufacturer;
        this.setSelectLoading(this.dom.model, true);

        try {
          const models = await fitmentApi.getNAModels({
            manufacturerId: this.#state.manufacturer,
            year: this.#state.year,
          });
          this.addSelectOptions(this.dom.model, models);
        } catch (error) {
          console.error(`[${this.name}] Error fetching models:`, error);
        } finally {
          this.setSelectLoading(this.dom.model, false);
        }
      }

      // Combobox needs this to update correct values
      this.#updatingFilters = true;
      this.dom.manufacturer.dispatchEvent(new Event('change', { bubbles: true }));
      this.#updatingFilters = false;
    }

    if (manufacturerChanged || this.#state.model !== oldState.model) {
      modelChanged = true;
      this.clearSelectOptions(this.dom.submodel);

      if(this.#state.year && this.#state.manufacturer && this.#state.model) {
        this.dom.model.value = this.#state.model;
        this.setSelectLoading(this.dom.submodel, true);

        try {
          const submodels = await fitmentApi.getNASubmodels({
            manufacturerId: this.#state.manufacturer,
            year: this.#state.year,
            modelId: this.#state.model,
          });
          this.addSelectOptions(this.dom.submodel, submodels);
        } catch (error) {
          console.error(`[${this.name}] Error fetching submodels:`, error);
        } finally {
          this.setSelectLoading(this.dom.submodel, false);
        }
      }
    }

    if (modelChanged || this.#state.submodel !== oldState.submodel) {
      if(this.#state.year && this.#state.manufacturer && this.#state.model && this.#state.submodel) {
        this.dom.submodel.value = this.#state.submodel;
        this.#cacheName(this.dom.submodel);
      }
    }

    // Enable/disable submit button based on state completeness
    if(Object.keys(this.#state).every(key => !!this.#state[key])) {
      this.dom.formSubmitBtn.disabled = false;
    } else {
      this.dom.formSubmitBtn.disabled = true;
    }
  }

  setSelectLoading(select: HTMLSelectElement, state: boolean) {
    if (state) {
      select.classList.add('loading');
    } else {
      select.classList.remove('loading');
    }
  }

  clearSelectOptions(select: HTMLSelectElement) {
    // Clear all but the first empty option
    Array.from(select.options).filter((opt) => opt.value !== '').forEach((opt) => {
      opt.remove();
    });

    select.value = '';
    select.disabled = true;
  }

  addSelectOptions(select: HTMLSelectElement, options: Array<{id: string; name: string;}>) {
    options.forEach((optionData) => {
      const option = new Option(optionData.name, optionData.id, false);
      select.add(option);
    });

    select.disabled = false;
  }

  #cacheName(select: HTMLSelectElement) {
    const selectName = select.name;
    const selectedOption = select.options[select.selectedIndex];
    if(selectedOption) {
      if (!this.#idNameCache[selectName]) {
        this.#idNameCache[selectName] = {};
      }
      this.#idNameCache[selectName]![selectedOption.value] = selectedOption.text;
    }
  }

  protected attachEventListeners() {
    this.dom.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.dom.form.addEventListener('reset', this.onFormReset.bind(this));
    this.dom.year.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.manufacturer.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.model.addEventListener('change', this.onSelectChange.bind(this));
    this.dom.submodel.addEventListener('change', this.onSelectChange.bind(this));

    // Delegated events for elements created dynamically
    this.dom.sizesContainer.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement;

      if(target.matches('select[name="size"]')) {
        // Size select event inside sizes container
        const sizeStr = target.value || '';

        if(sizeStr) {
          this.parent.onSizeSelect(sizeStr);
        }
      }
    });

    // Subscribe to user data changes
    this.subscribeToUserData();
  }

  /**
   * Subscribes to external userData changes.
   * Override in subclasses to disable or customize subscription.
   */
  protected subscribeToUserData() {
    userData.subscribe('vehicleNA', this.onUserDataChange.bind(this));
  }

  protected initCustomDropdowns() {
    this.#comboboxes.manufacturer = new AutocompleteCombobox({
      select: this.dom.manufacturer,
      cssClassPrefix: 'combobox',
      browserAutocomplete: 'off'
    });

    this.#comboboxes.model = new AutocompleteCombobox({
      select: this.dom.model,
      cssClassPrefix: 'combobox',
      browserAutocomplete: 'off'
    });
  }

  showErrorMessage(message: string) {
    if(!message) {
      return;
    }

    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if (textContainer) {
      textContainer.textContent = message;
    } else {
      this.dom.errorMessage.textContent = message;
    }

    this.dom.errorMessage.removeAttribute('hidden');
  }

  hideErrorMessage() {
    const textContainer = this.dom.errorMessage.querySelector('[data-error-message-text]');

    if(textContainer) {
      textContainer.textContent = '';
    } else {
      this.dom.errorMessage.textContent = '';
    }

    this.dom.errorMessage.setAttribute('hidden', 'hidden');
  }

  processTireSizes(vehicleTireSizes: Array<TireSize>): TireSize[] {
    this.log('processTireSizes()', vehicleTireSizes);

    // Match with available NT tires
    const availableSizes: TireSize[] = [];
    vehicleTireSizes.forEach(vSize => {
      // Check availability by comparing to sizeData
      const matches = this.parent.settings.sizeData.filter(item => {
        return vSize.size === item.size;
      });

      if(matches.length) {
        availableSizes.push(vSize);
      }
    });

    availableSizes.sort((a, b) => {
      // Sort by rim, width, height
      if (a.rim !== b.rim) {
        return a.rim - b.rim;
      } else if (a.width !== b.width) {
        return a.width - b.width;
      } else {
        return a.height - b.height;
      }
    });

    this.log('available sizes for vehicle', {vehicleTireSizes, availableSizes});

    return availableSizes;
  }

  showSizeOptions() {
    this.log('showSizeOptions()');

    const state = this.getState();
    const carModel = [
      state.manufacturer,
      state.model,
      this.getSubmodelName(),
      state.year,
    ].filter(Boolean).join(', ');

    const templateData = {
      carModel,
      sizes: this.availableSizes,
      sizesPlaceholder: t(`${this.parent.settings.lang}.tireselector__result__choose_size__placeholder`, {amount: this.availableSizes.length}),
    };

    this.log('Rendering sizes template with data:', templateData);

    this.dom.sizesContainer.innerHTML = Mustache.render(this.parent.sizesTemplate, templateData);
    this.dom.sizesContainer.hidden = false;

    if(!isInViewport(this.dom.sizesContainer)) {
      this.dom.sizesContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

  }

  clearSizeOptions() {
    this.dom.sizesContainer.innerHTML = '';
    this.dom.sizesContainer.hidden = true;
  }

  /**
   * Formats select value to string or null
   * All values are strings in select elements, make empty string null and others as strings
   */
  getSelectValue(select: HTMLSelectElement) {
    return select.value === '' ? null : String(select.value);
  }

  onSelectChange(event: Event) {
    // Callback was caused by internal change in filters
    if (this.#updatingFilters) {
      return;
    }

    const select = event.target as HTMLSelectElement;
    const selectName = select.name;
    const selectValue = this.getSelectValue(select);

    this.log('onSelectChange()', { selectName, selectValue });

    const newState = this.getState();
    let resetFields = false;
    for (let key in newState) {
      if (selectName === key) {
        newState[selectName] = selectValue;
        resetFields = true;
      } else if (resetFields) {
        newState[key] = null;
      }
    }

    this.setState(newState);
  }

  onFormSubmit(event: Event) {
    event.preventDefault();

    if (this.#loading) {
      return;
    }

    this.log('onFormSubmit()');

    this.#loading = true;
    this.parent.setLoading(true);

    this.persistVehicleSelection();

    const state = this.getState();
    this.parent.sendAnalyticsEvent({
      event: 'tyre_search_submit',
      tyre_search_type: 'Model NA Search',
      vehicle_manufacturer: state.manufacturer,
      vehicle_year: state.year,
      vehicle_model: state.model,
      vehicle_submodel: this.getSubmodelName() || state.submodel,
    });


    if (state.submodel) {
      const [modelId, chassisId] = state.submodel.split('|');

      fitmentApi.getNATireSizes({ modelId, chassisId })
        .then((data) => {
          this.log('got tire sizes for vehicle', data);
          this.availableSizes = this.processTireSizes(data);
          this.persistAvailableSizes(this.availableSizes);

          if (this.parent.settings.autoRedirect) {
            // If engine selected, dont show sizeOptions, but redirect to factory default size
            const selectedSize = this.availableSizes.find(size => size.default === true) || this.availableSizes[0];
            if (selectedSize?.size) {
              this.parent.onSizeSelect(selectedSize.size);
            } else {
              console.error(`[${this.name}]: no available sizes found for vehicle`);
              this.showSizeOptions(); // This handles also displaying message if no sizes found
            }
          } else {
            this.showSizeOptions();
          }
        })
        .catch((error) => {
          this.log('failed to get tire sizes for vehicle', error);
          const message = error.message || 'Failed to retrieve tire sizes. Please try again later.';
          this.showErrorMessage(message);
        })
        .finally(() => {
          this.#loading = false;
          this.parent.setLoading(false);
        });
    }
  }

  /**
   * Form reset clears user's data.
   * Customer request that all data should be cleared, not just vehicleNA data.
   * Clearing saved vehicleNA causes an UI update of this form
   */
  onFormReset(event: Event) {
    this.clearUserData();
  }

  /**
   * Persists vehicle selection to userData.
   * Override in subclasses to disable or customize persistence.
   */
  protected persistVehicleSelection() {
    const state = this.getState();
    userData.store({
      vehicleNA: {
        manufacturer: state.manufacturer!,
        year: state.year!,
        model: state.model!,
        submodel: state.submodel!,
        submodelName: this.getSubmodelName(),
      },
      vehicle: null,
      license: null,
      availableSizes: null,
      selectedSize: null,
    });
  }

  /**
   * Persists available sizes to userData.
   * Override in subclasses to disable or customize persistence.
   */
  protected persistAvailableSizes(sizes: TireSize[]) {
    userData.store({ availableSizes: sizes });
  }

  /**
   * Clears all persisted data from userData.
   * Override in subclasses to disable or customize clearing.
   */
  protected clearUserData() {
    userData.store({ license: null, vehicle: null, vehicleNA: null, availableSizes: null, selectedSize: null, selectedProduct: null });
  }

  // Helper method for accessing cached submodel name
  getSubmodelName(): string | null {
    const state = this.getState();
    return this.#idNameCache.submodel?.[state.submodel!] || null;
  }

  onUserDataChange(vehicleData: VehicleNA) {
    this.log('onUserDataChange()', vehicleData);

    const currentState = this.getState();

    if (vehicleData === null) {
      // Clear state
      for (let key in currentState) {
        currentState[key] = null;
      }

      this.setState(currentState);
      this.clearSizeOptions();
    } else {
      // Don't set state if nothing has changed
      if (Object.keys(currentState).every(key => currentState[key] === vehicleData[key])) {
        return;
      }

      this.setState({
        year: vehicleData?.year || null,
        manufacturer: vehicleData?.manufacturer || null,
        model: vehicleData?.model || null,
        submodel: vehicleData?.submodel || null,
      });

      this.clearSizeOptions();
    }
  }

  log(message: string, ...optionalParams: any[]) {
    console.log(`[${this.name}] ${message}`, ...optionalParams, this.dom.root);
  }
}

export default ModelNA;
